import { CheckOutlined, UndoOutlined } from '@ant-design/icons';
import { Button, Col, Row, Space, Tooltip } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import receivableEmbargoBankAccountBankApi from '../../../../apis/ReceivableEmbargoBankAccounBankApi';
import CustomContext from '../../../../contexts/CustomContext';
import { Receivable, ReceivableEmbargoBankAccount, ReceivableEmbargoBankAccountBank } from '../../../../models/Entities';
import alertService from '../../../../services/AlertService';
import rolesService from '../../../../services/RolesService';
import ReceivableCancellationModal from '../ReceivableCancellationModal/ReceivableCancellationModal';
import ReceivablePaymentModal from '../ReceivablePaymentModal/ReceivablePaymentModal';
import ReceivableEmbargoBankAccountBankComponent from './ReceivableEmbargoBankAccountBankComponent/ReceivableEmbargoBankAccountBankComponent';
import styles from './ReceivableEmbargoBankAccountComponent.module.scss';

const ReceivableEmbargoBankAccountComponent = (props: Props) => {
    const { receivable, embargoBankAccount, onUpdate, readonly } = props;

    /*** HOOKS ***/

    const intl = useIntl();
    const { auth } = useContext(CustomContext);
    const [receivableEmbargoBankAccountBanks, setReceivableEmbargoBankAccountBanks] = useState<ReceivableEmbargoBankAccountBank[]>([]);
    const [paymentModalVisible, setPaymentModalVisible] = useState<boolean>(false);
    const [cancellationModalVisible, setCancellationModalVisible] = useState<boolean>(false);
    const [, setLoading] = useState<'loading' | 'refreshing'>();

    /*** EFFECTS ***/

    // list council banks
    useEffect(() => {
        const init = async () => {
            try {
                setLoading('loading');
                const receivableEmbargoBankAccountBankPage = await receivableEmbargoBankAccountBankApi.list(0, 1000, 'id', true, embargoBankAccount.id!);
                setReceivableEmbargoBankAccountBanks(receivableEmbargoBankAccountBankPage.content);
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [embargoBankAccount.id, intl]);

    /*** METHODS ***/

    const save = async () => {
        try {
            setPaymentModalVisible(false);
            setCancellationModalVisible(false);
            await onUpdate();
        } catch (error) {
            alertService.displayError(error, intl);
        }
    };

    const refresh = async () => {
        try {
            setLoading('loading');
            const receivableEmbargoBankAccountBankPage = await receivableEmbargoBankAccountBankApi.list(0, 1000, 'id', true, embargoBankAccount.id!);
            setReceivableEmbargoBankAccountBanks(receivableEmbargoBankAccountBankPage.content);
        } catch (error) {
            alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    /*** VISUAL ***/

    const isAdmin = rolesService.hasAnyRole(auth, ['ROLE_ADMIN']);

    return (
        <>
            <Row gutter={[28, 0]}>
                <Col span={24}>
                    {receivableEmbargoBankAccountBanks.map((receivableEmbargoBankAccountBank) => (
                        <ReceivableEmbargoBankAccountBankComponent
                            key={receivableEmbargoBankAccountBank.id}
                            receivableEmbargoBankAccountBankId={receivableEmbargoBankAccountBank.id}
                            readonly={readonly}
                            onUpdate={refresh}
                        />
                    ))}
                </Col>
            </Row>
            <Row gutter={[28, 0]} className={styles.buttons}>
                <Col span={24}>
                    <Space>
                        {isAdmin && !readonly && (
                            <Tooltip key="paid" title={<FormattedMessage id="receivable.paid.tooltip" />} mouseLeaveDelay={0}>
                                <Button type="primary" danger icon={<CheckOutlined />} onClick={() => setPaymentModalVisible(true)}>
                                    <FormattedMessage id="receivable.paid" tagName="span" />
                                </Button>
                            </Tooltip>
                        )}
                        {isAdmin && !readonly && (
                            <Button key="cancel" icon={<UndoOutlined />} onClick={() => setCancellationModalVisible(true)}>
                                <FormattedMessage id="receivable.cancellation.button" tagName="span" />
                            </Button>
                        )}
                    </Space>
                </Col>
            </Row>

            {paymentModalVisible && receivable && (
                <ReceivablePaymentModal receivable={receivable} onCancel={() => setPaymentModalVisible(false)} onSave={save} />
            )}
            {cancellationModalVisible && (
                <ReceivableCancellationModal receivable={receivable} onCancel={() => setCancellationModalVisible(false)} onSave={save} />
            )}
        </>
    );
};
export default ReceivableEmbargoBankAccountComponent;

interface Props {
    receivable: Receivable;
    embargoBankAccount: ReceivableEmbargoBankAccount;
    onUpdate: () => Promise<void>;
    readonly: boolean;
}
